import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../../styles/admin/admin-table.css";
import "../../styles/admin/admin-form.css";
import config from "../../config";

const API_BASE_URL = String(config.apiBaseUrl || "").replace(/\/+$/, "");

export default function AdminUsers() {
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [estado, setEstado] = useState("TODOS");
  const [pais, setPais] = useState("TODOS");

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("token");

      if (!token) {
        throw new Error("No hay sesión activa. Inicia sesión nuevamente.");
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/users`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const responseText = await response.text();

      if (!response.ok) {
        throw new Error(responseText || "No se pudo cargar la lista de usuarios");
      }

      const data = responseText ? JSON.parse(responseText) : [];
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || "Ocurrió un error al cargar los usuarios");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return "-";

    try {
      return new Date(value).toLocaleDateString("es-CO");
    } catch {
      return "-";
    }
  };

  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      const fullName = `${user.name || ""} ${user.lastName || ""}`.trim().toLowerCase();
      const email = (user.email || "").toLowerCase();
      const dni = (user.dni || "").toLowerCase();
      const phone = (user.phone || "").toLowerCase();
      const country = (user.country || "").toUpperCase();
      const status = (user.status || "").toUpperCase();

      const searchValue = search.toLowerCase().trim();

      const matchesSearch =
        !searchValue ||
        fullName.includes(searchValue) ||
        email.includes(searchValue) ||
        dni.includes(searchValue) ||
        phone.includes(searchValue);

      const matchesEstado = estado === "TODOS" || status === estado;

      const matchesPais = pais === "TODOS" || country === pais;

      return matchesSearch && matchesEstado && matchesPais;
    });
  }, [users, search, estado, pais]);

  return (
    <div className="admin-page">
      <div className="table-page-header">
        <div>
          <h2>Usuarios</h2>
        </div>
      </div>

      <div className="table-toolbar">
        <input
          className="table-search"
          type="text"
          placeholder="Buscar por nombre, correo, cédula o celular"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className="table-filters">
          <select value={estado} onChange={(e) => setEstado(e.target.value)}>
            <option value="TODOS">Todos los estados</option>
            <option value="PENDING_PAYMENT">Pendiente pago</option>
            <option value="TRIAL_ACTIVE">Prueba activa</option>
            <option value="ACTIVE">Activos</option>
            <option value="BLOCKED">Bloqueados</option>
            <option value="CANCELED">Cancelados</option>
          </select>

          <select value={pais} onChange={(e) => setPais(e.target.value)}>
            <option value="TODOS">Todos los países</option>
            <option value="CO">Colombia</option>
            <option value="EC">Ecuador</option>
          </select>

          <button type="button" className="secondary-btn" onClick={loadUsers}>
            Recargar
          </button>
        </div>
      </div>

      {error ? <p className="admin-form-message error">{error}</p> : null}

      <div className="table-wrapper">
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Nombre completo</th>
              <th>Correo</th>
              <th>Celular</th>
              <th>Cédula</th>
              <th>País</th>
              <th>Estado</th>
              <th>Suscripción activa</th>
              <th>Próximo cobro</th>
              <th>Fecha registro</th>
              <th>Acciones</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="11" className="empty-row">
                  Cargando usuarios...
                </td>
              </tr>
            ) : filteredUsers.length > 0 ? (
              filteredUsers.map((user) => {
                const fullName = `${user.name || ""} ${user.lastName || ""}`.trim();
                const status = (user.status || "").toUpperCase();
                const isActive = status === "ACTIVE";

                return (
                  <tr key={user.idUser}>
                    <td>{user.idUser}</td>
                    <td>{fullName || "-"}</td>
                    <td>{user.email || "-"}</td>
                    <td>{user.phone || "-"}</td>
                    <td>{user.dni || "-"}</td>
                    <td>{user.country || "-"}</td>
                    <td>
                      <span className={`status-badge ${isActive ? "active" : "inactive"}`}>
                        {status || "-"}
                      </span>
                    </td>
                    <td>{user.subscriptionActive ? "Sí" : "No"}</td>
                    <td>{formatDate(user.nextChargeAt)}</td>
                    <td>{formatDate(user.registerDate)}</td>
                    <td>
                      <div className="table-actions">
                        <button onClick={() => navigate(`/admin/usuarios/${user.idUser}`)}>
                          Ver
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="11" className="empty-row">
                  No se encontraron usuarios.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}